import {useLazyQuery as useApolloLazyQuery} from '@apollo/client';
import {useReports} from '@sail/observability';
import {useMemoizedValue} from '@sail/react';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import useGetApolloContext from 'src/internal/apollo/useGetApolloContext';
import {getOperationName} from 'src/internal/shared/utils/gql';
import refetchOnSkipOperationAllowlist from 'src/public/graphql/refetchOnSkipOperationAllowlist';

import type {
  LazyQueryPromiseReturn,
  QueryRefetchReturn,
  UseLazyQueryOptions,
  UseLazyQueryReturn,
} from 'src/internal/apollo/types';
import type {
  GraphQlDocument,
  VariablesOfDoc,
} from 'src/internal/shared/graphql/types/document';

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-data/api/useLazyQuery
 */
export default function useLazyQuery<
  /** The data returned by the GraphQL query (inferred from the passed GraphQL document). */
  TData,
  /** The variables that the GraphQL query accept (inferred from the passed GraphQL document). */
  TVariables,
  /** The permissions needed by the GraphQL query (inferred from the passed GraphQL document). */
  TPermissions,
  /** (internal) The variables passed to the lazy query via the hook (inferred from the passed options). */
  THookVariables extends Partial<TVariables> | undefined = undefined,
  TokenIsOptional extends boolean = false,
>(
  /**
   * The GraphQL query that is going to be executed. This must be created with the
   * [`gql`](/apis/data/gql) or
   * [`graphql`](/apis/data/graphql) template literal.
   */
  query: GraphQlDocument<TData, TVariables, TPermissions>,
  /**
   * Optional options to pass to the query.
   * Refer to [operation options](/data/guides/graphql-usage/the-basics-runtime#operation-options) docs.
   */
  options: UseLazyQueryOptions<
    GraphQlDocument<TData, TVariables, TPermissions>,
    THookVariables
  > = {},
): /**
 * Array with two elements:
 *
 * **callback**: Function to execute the actual query. Accepts an options object that will override any options passed to the hook (including the variables and the permissions token).
 *
 * **result**: Object with `data`, `loading` and `error` (similar to the one that the
 * [`useQuery` Apollo hook](https://www.apollographql.com/docs/react/api/react/hooks/#result) returns).
 */
UseLazyQueryReturn<
  GraphQlDocument<TData, TVariables, TPermissions>,
  THookVariables,
  TokenIsOptional
> {
  const baseContext = useGetApolloContext(options);
  const {error, warning} = useReports();

  const operationName = useMemo(() => {
    return getOperationName(query) || '';
  }, [query]);
  const refetchOnSkipIsAllowed =
    refetchOnSkipOperationAllowlist.includes(operationName);

  const queryOptions = useMemo(
    () => ({
      ...options,
      context: baseContext,
    }),
    [baseContext, options],
  );

  const {hookVariables, hookOptionsWithoutVariables} = useMemo(() => {
    const {variables, ...rest} = queryOptions;

    return {
      hookVariables: variables,
      hookOptionsWithoutVariables: rest,
    };
  }, [queryOptions]);

  const memoizedHookVariables = useMemoizedValue(hookVariables);
  const previousCallbackVariablesRef = useRef<Partial<TVariables>>();

  const [callback, result] = useApolloLazyQuery(
    query,
    hookOptionsWithoutVariables,
  );
  const {refetch: originalRefetch} = result;

  const callbackWrapped = useCallback(
    async (
      ...restArgs: Parameters<
        UseLazyQueryReturn<
          GraphQlDocument<TData, TVariables, TPermissions>,
          THookVariables,
          TokenIsOptional
        >[0]
      >
    ): Promise<
      LazyQueryPromiseReturn<
        GraphQlDocument<TData, TVariables, TPermissions>,
        THookVariables
      >
    > => {
      const cbOptions = restArgs[0];

      const clearedPreviousVariables = Object.fromEntries(
        Object.keys(previousCallbackVariablesRef.current ?? {}).map((key) => [
          key,
          undefined,
        ]),
      );
      const mergedVariables = {
        ...clearedPreviousVariables,
        ...memoizedHookVariables,
        ...cbOptions?.variables,
      } as VariablesOfDoc<GraphQlDocument<TData, TVariables, TPermissions>>;
      previousCallbackVariablesRef.current = mergedVariables;

      const mergedOptions = {
        ...cbOptions,
        variables: mergedVariables,
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {refetch, ...result} = await callback(mergedOptions);

      return result;
    },
    [callback, memoizedHookVariables],
  );

  const currentRefetchResult = useMemo<
    QueryRefetchReturn<GraphQlDocument<TData, TVariables, TPermissions>>
  >(() => {
    return {
      data: result.data,
      error: result.error,
      errors: result.error?.graphQLErrors,
      loading: result.loading,
      networkStatus: result.networkStatus,
    };
  }, [result.data, result.error, result.loading, result.networkStatus]);

  const currentRefetchResultRef = useRef(currentRefetchResult);

  useEffect(() => {
    if (queryOptions.skip !== true) {
      currentRefetchResultRef.current = currentRefetchResult;
    }
  }, [currentRefetchResult, queryOptions.skip]);

  const refetchWrapped = useCallback(
    (...refetchArgs: Parameters<typeof originalRefetch>) => {
      if (queryOptions.skip === true) {
        if (!refetchOnSkipIsAllowed) {
          error('Refetch called when skip is true in useLazyQuery', {
            extras: {
              operationName,
              service: baseContext.service,
              component: baseContext.component,
              queryProject: baseContext.queryProject,
            },
          });

          return Promise.resolve(currentRefetchResultRef.current);
        }

        warning(
          'Refetch called when skip is true in useLazyQuery. go/remove-refetch-when-skip for details on how to kill this warning',
          {
            extras: {
              operationName,
              service: baseContext.service,
              component: baseContext.component,
              queryProject: baseContext.queryProject,
            },
          },
        );
      }

      return originalRefetch(...refetchArgs);
    },
    [
      baseContext.component,
      baseContext.queryProject,
      baseContext.service,
      currentRefetchResultRef,
      originalRefetch,
      operationName,
      queryOptions.skip,
      refetchOnSkipIsAllowed,
      error,
      warning,
    ],
  );

  return [callbackWrapped, {...result, refetch: refetchWrapped}];
}
